import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Select,
  Box,
} from "@chakra-ui/react";
import Multiselect from "multiselect-react-dropdown";
import { Formik, Form } from "formik";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import CustomInput from "components/common/customInput/CustomInput";
import { RegisterValidation } from "components/Auth/validations/validation";

/* ================= TYPES ================= */

type CreateFormValues = {
  id?: string;
  profileId: string; // ✅ mandatory
  name: string;
  moNumber: string;
  email: string;
  password?: string;
  confirmPassword?: string;
  permission: string[];
  role: string;
};

type ModalProps = {
  isOpen: boolean;
  isPassword: boolean;
  onClose: () => void;
  handleGetUser: () => void;
  data?: any;
  type: "Add" | "Edit";
};

type RoleValues = {
  name: string;
  permissions: string[];
};

/* ================= COMPONENT ================= */

export default function BsesUserModal({
  isOpen,
  onClose,
  handleGetUser,
  data,
  type,
}: ModalProps): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [roles, setRoles] = useState<RoleValues[]>([]);
  const [permissions, setPermissions] = useState<{ key: string }[]>([]);
  const [showError, setShowError] = useState(false);

  /* ---------------- Fetch Roles ---------------- */
  useEffect(() => {
    getRoles();
  }, []);

  const getRoles = async () => {
    try {
      const res: any = await appRequest("userrole", "get", {});
      setRoles(Array.isArray(res) ? res : []);
    } catch (err: any) {
      toastNotify({
        title: "Error",
        message: err?.message || "Failed to load roles",
        type: "error",
      });
    }
  };

  /* ---------------- Role → Permission Mapper ---------------- */
  const mapPermissions = (roleName: string, setFieldValue: any) => {
    const role = roles.find((r) => r.name === roleName);
    const mapped = role?.permissions?.map((p) => ({ key: p })) || [];

    setPermissions(mapped);
    setFieldValue(
      "permission",
      mapped.map((m) => m.key)
    );
  };

  /* ---------------- Initial Values ---------------- */
  const initialValues: CreateFormValues = {
    id: data?.id || "",
    profileId: data?.profileId || "", // ✅ added
    name: data?.name || "",
    moNumber: data?.moNumber || "",
    email: data?.email || "",
    password: "",
    confirmPassword: "",
    role: data?.role || "user",
    permission: data?.permission || [],
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "sm", md: "xl", lg: "2xl" }}
      isCentered
    >
      <ModalOverlay />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={type === "Add" ? RegisterValidation : undefined}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const payload = { ...values };

            const res: any = await appRequest(
              "user",
              type === "Add" ? "create" : "update",
              payload
            );

            if (res?.status === "success") {
              toastNotify({
                title: `User ${type === "Add" ? "created" : "updated"
                  } successfully`,
                type: "success",
              });
              handleGetUser();
              onClose();
            } else {
              throw new Error(res?.message);
            }
          } catch (err: any) {
            toastNotify({
              title: "Operation failed",
              message: err?.message || "Something went wrong",
              type: "error",
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          handleChange,
          handleSubmit,
          setFieldValue,
          values,
          errors,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <ModalContent>
              <ModalHeader fontWeight="700" color="teal.400">
                {type} User
              </ModalHeader>
              <ModalCloseButton />

              <ModalBody maxH="60vh" overflowY="auto">
                {/* ===== PROFILE ID (MANDATORY) ===== */}
                <Grid templateColumns="1fr 1fr" gap={4} mt={4}>
                  <CustomInput
                    label="User Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={errors.name}
                    showError={showError}
                  />
                  <CustomInput
                    label="Profile ID"
                    name="profileId"
                    value={values.profileId}
                    onChange={handleChange}
                    error={errors.profileId}
                    showError={showError}
                  />
                  <Box display="none">
                    <CustomInput
                      label="Mobile Number"
                      name="moNumber"
                      type="number"
                      value={values.moNumber}
                      onChange={handleChange}
                      error={errors.moNumber}
                      showError={showError}
                    />
                  </Box>
                </Grid>
                <Grid templateColumns="1fr" gap={4} mt={4}>
                  <CustomInput
                    label="Email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={errors.email}
                    showError={showError}
                  />
                </Grid>


                {/* ===== PASSWORD (ADD ONLY) ===== */}
                {type === "Add" && (
                  <Grid templateColumns="1fr 1fr" gap={4} mt={4}>
                    <CustomInput
                      label="Password"
                      name="password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      error={errors.password}
                      showError={showError}
                    />
                    <CustomInput
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      error={errors.confirmPassword}
                      showError={showError}
                    />
                  </Grid>
                )}

                {/* ===== ROLE ===== */}
                <Grid display="none" templateColumns="1fr" gap={4} mt={4}>
                  <FormControl>
                    <FormLabel>Select Role</FormLabel>
                    <Select
                      name="role"
                      value={values.role}
                      onChange={(e) => {
                        setFieldValue("role", e.target.value);
                        mapPermissions(e.target.value, setFieldValue);
                      }}
                    >
                      {roles.map((r) => (
                        <option key={r.name} value={r.name}>
                          {r.name === "default" ? "User" : r.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Permissions kept hidden (system controlled) */}
                  <FormControl display="none">
                    <Multiselect
                      displayValue="key"
                      options={permissions}
                      selectedValues={permissions.filter((p) =>
                        values.permission.includes(p.key)
                      )}
                      onSelect={(list) =>
                        setFieldValue(
                          "permission",
                          list.map((l: any) => l.key)
                        )
                      }
                      onRemove={(list) =>
                        setFieldValue(
                          "permission",
                          list.map((l: any) => l.key)
                        )
                      }
                      showCheckbox
                    />
                  </FormControl>
                </Grid>
              </ModalBody>

              <ModalFooter>
                <Button mr={4} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  bg="teal.400"
                  color="white"
                  _hover={{ bg: "teal.500" }}
                  isLoading={isSubmitting}
                  onClick={() => setShowError(true)}
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalContent>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
